import {
  Breakpoint,
  Color,
  DefinitionCard,
  EquationDiagram,
  EquationDiagramBoxData,
  EquationDiagramOperator,
  FontWeight,
  InfoPair,
  SideNote,
  TextGrid,
  TilesGrid,
  Typography,
  TypographyElement,
  TypographyVariant,
} from '@elearning-platform/ui';
import React from 'react';
import { useMediaQuery } from 'react-responsive';

import { CenteredSlideCustomComponent, CenteredSlideType } from '../../enums';
import { DefinitionCardData, EquationDiagramData, InfoPairData } from '../../interfaces';

import {
  BrandHeader,
  CenteredTextContainer,
  CenteredTextContent,
  CenteredTextWrapper,
  DefinitionCardWrapper,
  DoubleCenteredTextBox,
  DoubleCenteredTextBoxText,
  DoubleCenteredTextContainer,
  DoubleCenteredTextContent,
  DoubleCenteredTextContentContainer,
  DoubleCenteredTextWrapper,
  HeadersWrapper,
  Image,
  ImageWrapper,
  InfoPairContainer,
  InfoPairTextBox,
  InfoPairWrapper,
  List,
  ListItem,
  StyledBoxWithImages,
  StyledMarkdown,
  TextBox,
  TextBoxWrapper,
  TextGridWrapper,
  TextWrapper,
  Wrapper,
} from './centeredSlide.styled';
import { CenteredSlideProps } from './centeredSlide.types';

export const CenteredSlide: React.FC<CenteredSlideProps> = (props: CenteredSlideProps): JSX.Element => {
  const { baseUrl, data }: CenteredSlideProps = props;
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });

  return (
    <Wrapper>
      <HeadersWrapper>
        {!!data.commonText && (
          <TextWrapper>
            <Typography tag={TypographyElement.div} variant={TypographyVariant.TextLG}>
              <StyledMarkdown text={data.commonText} />
            </Typography>
          </TextWrapper>
        )}
        {!!data.header && (
          <TextWrapper>
            <Typography fontWeight={FontWeight.SemiBold} tag={TypographyElement.div} variant={TypographyVariant.TextLG}>
              <StyledMarkdown text={data.header} />
            </Typography>
          </TextWrapper>
        )}
        {!!data.brandHeader && (
          <TextWrapper>
            <BrandHeader fontWeight={FontWeight.Bold} variant={TypographyVariant.DisplayXS}>
              {data.brandHeader}
            </BrandHeader>
          </TextWrapper>
        )}
      </HeadersWrapper>
      {data.contentType === CenteredSlideType.CustomComponent && ((): JSX.Element => {
        switch (data.customComponentData.component) {
          case CenteredSlideCustomComponent.Equation:
            return (
              <EquationDiagram
                boxes={data.customComponentData.boxes.map((box: EquationDiagramData): EquationDiagramBoxData => ({
                  ...box,
                  bgColor: box.bgColor as Color,
                }))}
                operators={data.customComponentData.operators
                  ? data.customComponentData.operators.map((operator: EquationDiagramOperator): EquationDiagramOperator => operator)
                  : undefined
                }
              />
            );
          case CenteredSlideCustomComponent.TilesGrid:
            return (
              <TilesGrid tilesData={data.customComponentData.componentData} />
            );
          case CenteredSlideCustomComponent.DefinitionCard:
            return (
              <DefinitionCardWrapper>
                {data.customComponentData.componentData.map((card: DefinitionCardData): JSX.Element => (
                  <DefinitionCard header={card.header} image={baseUrl + card.image} key={card.header} text={card.text} theme={card.theme} />
                ))}
              </DefinitionCardWrapper>
            );
          case CenteredSlideCustomComponent.InfoPair:
            return (
              <InfoPairWrapper>
                <InfoPairContainer>
                  {data.customComponentData.componentData.map((pair: InfoPairData): JSX.Element => (
                    <InfoPair header={pair.header} key={pair.header} text={pair.text} />
                  ))}
                </InfoPairContainer>
                {!!data.customComponentData.boxText && (
                  <InfoPairTextBox>
                    <Typography tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
                      <StyledMarkdown text={data.customComponentData.boxText} />
                    </Typography>
                  </InfoPairTextBox>
                )}
              </InfoPairWrapper>
            );
          case CenteredSlideCustomComponent.TextGrid:
            return (
              <TextGridWrapper>
                <TextGrid texts={data.customComponentData.componentData} />
              </TextGridWrapper>
            );
          case CenteredSlideCustomComponent.BoxWithImages:
            return (
              <StyledBoxWithImages
                $withDoubleImage={!!data.customComponentData.componentData.leftImage && !!data.customComponentData.componentData.rightImage}
                leftImage={baseUrl + data.customComponentData.componentData.leftImage}
                primaryHeader={data.customComponentData.componentData.primaryHeader}
                rightImage={data.customComponentData.componentData.rightImage
                  ? baseUrl + data.customComponentData.componentData.rightImage
                  : undefined
                }
                secondaryHeader={data.customComponentData.componentData.secondaryHeader}
                text={data.customComponentData.componentData.text}
              />
            );
          default:
            return <div />;
        }
      })()}
      {data.contentType === CenteredSlideType.Image && (
        <ImageWrapper $isWideImage={!!data.isWideImage}>
          <Image src={isSmallDesktop ? baseUrl + data.desktopImage : baseUrl + data.mobileImage} />
          {!!data.supportingText && (
            <Typography fontWeight={FontWeight.Bold} variant={TypographyVariant.TextMD}>
              {data.supportingText}
            </Typography>
          )}
        </ImageWrapper>
      )}
      {data.contentType === CenteredSlideType.CenteredText && (
        <CenteredTextWrapper>
          <CenteredTextContainer>
            <Typography tag={TypographyElement.div} variant={TypographyVariant.TextMD}>
              <CenteredTextContent text={data.text} />
            </Typography>
          </CenteredTextContainer>
          {!!data.textGridData && (
            <TextBoxWrapper>
              {data.textGridData.map((text: string): JSX.Element => (
                <TextBox key={text}>
                  <Typography tag={TypographyElement.div} variant={TypographyVariant.TextLG}>
                    <StyledMarkdown text={text} />
                  </Typography>
                </TextBox>
              ))}
            </TextBoxWrapper>
          )}
          {!!data.sideNoteData && (
            <SideNote
              header={data.sideNoteData.header}
              image={data.sideNoteData.image ? baseUrl + data.sideNoteData.image : undefined}
              list={data.sideNoteData.list}
              text={data.sideNoteData?.text}
              withCenteredContent={data.sideNoteData.withCenteredContent}
              withSideImage={data.sideNoteData.withSideImage}
            />
          )}
        </CenteredTextWrapper>
      )}
      {data.contentType === CenteredSlideType.DoubleCenteredText && (
        <DoubleCenteredTextWrapper>
          <DoubleCenteredTextContentContainer>
            <DoubleCenteredTextContainer>
              {!!data.leftText && (
                <Typography tag={TypographyElement.div} variant={TypographyVariant.TextMD}>
                  <DoubleCenteredTextContent text={data.leftText} />
                </Typography>
              )}
              {!!data.leftList && (
                <List>
                  {data.leftList.map((item: string): JSX.Element => (
                    <ListItem key={item}>
                      <Typography tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
                        <StyledMarkdown text={item} />
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              )}
            </DoubleCenteredTextContainer>
            <DoubleCenteredTextContainer>
              {!!data.rightText && (
                <Typography tag={TypographyElement.div} variant={TypographyVariant.TextMD}>
                  <DoubleCenteredTextContent text={data.rightText} />
                </Typography>
              )}
              {!!data.rightList && (
                <List>
                  {data.rightList.map((item: string): JSX.Element => (
                    <ListItem key={item}>
                      <Typography tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
                        <StyledMarkdown text={item} />
                      </Typography>
                    </ListItem>
                  ))}
                </List>
              )}
            </DoubleCenteredTextContainer>
          </DoubleCenteredTextContentContainer>
          {!!data.boxText && (
            <DoubleCenteredTextBox>
              <Typography tag={TypographyElement.div} variant={TypographyVariant.TextSM}>
                <DoubleCenteredTextBoxText text={data.boxText} />
              </Typography>
            </DoubleCenteredTextBox>
          )}
        </DoubleCenteredTextWrapper>
      )}
    </Wrapper>
  );
};
